//****************************************************************************
//
// Copyright(C) 2025, CyberTAN Technology, Inc. All Rights Reserved.
// This source code contains confidential and proprietary
// information of CyberTAN Technology, Inc. Unauthorized reproduction,
// modification, distribution, or disclosure of this file, in whole or in part,
// by any means, is strictly prohibited without prior written permission from
// CyberTAN Technology, Inc.
//
// THIS SOURCE CODE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
// EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
// MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NONINFRINGEMENT.
//
// IN NO EVENT SHALL CYBERTAN TECHNOLOGY, INC. BE LIABLE FOR ANY CLAIM,
// DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
// TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOURCE CODE.
//
//****************************************************************************

import { AxiosResponse } from 'axios';
import { cbtRedirectDto } from 'dto/cbt-redirect.dto';
import { CreateRontoSiteDto } from 'dto/create-ronto-site.dto';
import { MembersMineInviteDto } from 'dto/members-mine-invite.dto';
import { PortalRegisterDto } from 'dto/portal-register.dto';
import { ResetPasswordDto, SendEmailDto } from 'dto/reset-password.dto';
import { SignInDto } from 'dto/signin.dto';
import { UpdatePortalUserPasswordCheckDto } from 'dto/update-portal-user-password-check.dto';
import { UpdateSiteDto } from 'dto/update-site.dto';
import { UpdateSystemParDeviceConfigDto } from 'dto/update-system-par-device-config.dto';
import { UpdateSystemDto } from 'dto/update-systems-query.dto';
import { User } from 'entities/user.entity';
import { ComplexDeviceWithType } from 'models/complex-device-with-type';
import { GetPortalMembers } from 'models/get-portal-member';
import { MailServerSetting } from 'models/mail-server-setting.model';
import { Oauth2 } from 'models/oauth2';
import { ParDeviceConfig } from 'models/par-device-config.model';
import { SiteDevices } from 'models/site-devices-model';
import { SiteInfo } from 'models/site-info';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axiosInstance from 'utils/axiosInstance';
import { BuildVersionList } from 'utils/helper';
import { getItem, setItem } from 'utils/localStorageHelper';
import { useConfig } from './ConfigProvider';

const AuthPortalContext = React.createContext<{
  token: string;
  userId: string;
  password: string;
  user: User;
  signInByUser: (params: SignInDto) => void;
  signUpByUser: (params: PortalRegisterDto) => Promise<AxiosResponse>;
  sendEmailByUser: (params: SendEmailDto) => Promise<AxiosResponse>;
  checkResetToken: (token: string) => Promise<{ valid: boolean; reason: string }>;
  resetPasswordByUser: (params: ResetPasswordDto, token: string) => Promise<AxiosResponse>;
  signInToRemoteSite: (siteName: string) => Promise<AxiosResponse<Oauth2>>;
  storageClearThenGoBack: () => void;
  logout: () => void;
  getUser: () => void;
  putUserUpdateMe: (namePassword: UpdatePortalUserPasswordCheckDto) => Promise<AxiosResponse>;
  deleteSite: (siteId: number) => Promise<AxiosResponse>;
  getMembersByLeaderId: (id: number) => Promise<AxiosResponse<GetPortalMembers[]>>;
  deleteMemberByEmail: (memberEmail: string) => Promise<AxiosResponse>;
  postMemberInvite: (data: MembersMineInviteDto) => Promise<AxiosResponse>;
  getSites: () => Promise<AxiosResponse<SiteInfo[]>>;
  getDevicesBySiteId: (siteId: number) => Promise<AxiosResponse<SiteDevices>>;
  getComplexDevices: ({
    includeStatistics,
    includeCapabilities,
    includeBackup,
  }: {
    includeStatistics?: boolean;
    includeCapabilities?: boolean;
    includeBackup?: boolean;
  }) => Promise<AxiosResponse<ComplexDeviceWithType[]>>;
  postCbtRedirect: (data: cbtRedirectDto) => Promise<AxiosResponse>;
  getSiteInfoById: (siteId: number) => Promise<AxiosResponse<SiteInfo>>;
  postRontoSite: (createRontoDto: CreateRontoSiteDto) => Promise<AxiosResponse<SiteInfo>>;
  putSite: (siteId: number, updateSite: UpdateSiteDto) => Promise<AxiosResponse<SiteInfo>>;
  getSystemsParDevicePortalConfig: (configs: string[]) => Promise<AxiosResponse<ParDeviceConfig>>;
  putSystemsParDevicePortalConfigQuery: (parDeviceConfigQuery: UpdateSystemParDeviceConfigDto) => Promise<AxiosResponse<ParDeviceConfig>>;
  getSystemsMailServerSetting: () => Promise<AxiosResponse<MailServerSetting>>;
  putSystemsMailServerSetting: (updateMailServerSetting: UpdateSystemDto) => Promise<AxiosResponse<MailServerSetting>>;
  getPortalBackup: () => Promise<AxiosResponse>;
  putPortalRestore: (file: FormData) => Promise<AxiosResponse>;
  getCheckApiIsReady: () => Promise<AxiosResponse<boolean>>;
}>(null!);

export const AuthPortalProvider = ({ children }: { children: React.ReactNode }) => {
  const { buildVersion, cbtApiUrl, countryCode, getDefaultConfig } = useConfig();
  const storageToken = getItem('access_token') ?? '';
  const storageUserId = getItem('userId') ?? '';
  const [token, setToken] = useState<string>(storageToken);
  const [userId, setUserId] = useState<string>(storageUserId);
  const [password, setPassword] = useState<string>('');
  const [user, setUser] = useState<User>({} as User);
  const storageSiteId = getItem('siteId') ?? '';

  const interceptorIdRef = useRef<number | null>(null);

  useEffect(() => {
    /* NOTE: 為 portal UI 時，從 backend-cloud 做 refresh access token */
    if ((buildVersion === BuildVersionList.portal && cbtApiUrl) !== undefined) {
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.response.eject(interceptorIdRef.current);
      }

      interceptorIdRef.current = axiosInstance.interceptors.response.use(
        // Success actions
        undefined,
        async (error: any) => {
          switch (error.response.status) {
            case 401:
              const newToken = await refreshAccessToken();
              // 使用新 Token 重新發送原始請求
              error.config.headers.Authorization = `Bearer ${newToken}`;
              return axiosInstance.request(error.config);
            default:
              break;
          }
          return Promise.reject(error);
        }
      );
    }
    return () => {
      if (interceptorIdRef.current !== null) {
        axiosInstance.interceptors.response.eject(interceptorIdRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildVersion, cbtApiUrl]);

  useEffect(() => {
    const currentToken = getItem('access_token') ?? '';
    if (buildVersion === BuildVersionList.portal && currentToken !== '' && token !== '' && cbtApiUrl !== undefined) {
      getUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildVersion, token, cbtApiUrl]);

  useEffect(() => {
    // 因進入 Ronto site，會重新取得 countryCode, radioChannelWhiteList。為了刷新 countryCode, radioChannelWhiteList 供 Create New Site 的 Country 用
    if (buildVersion === BuildVersionList.portal && storageSiteId === '' && countryCode !== '') {
      getDefaultConfig();
    }
  }, [buildVersion, countryCode, storageSiteId, getDefaultConfig]);

  const signInByUser = (params: SignInDto) => {
    setPassword(params.password);
    return axiosInstance.post<Oauth2>(`${cbtApiUrl}portal/api/oauth2`, params).then((response: AxiosResponse<Oauth2>) => {
      const { access_token, username, refresh_token, portalRole } = response.data;
      setItem('access_token', access_token);
      setItem('refresh_token', refresh_token);
      setToken(access_token);
      setItem('userId', username);
      setUserId(username);
      setItem('currentRole', portalRole);
    });
  };

  const signUpByUser = (params: PortalRegisterDto) => {
    return axiosInstance.post(`${cbtApiUrl}portal/api/users/register`, params);
  };

  const sendEmailByUser = (params: SendEmailDto) => {
    return axiosInstance.put(`${cbtApiUrl}portal/api/users/resetPassword`, params);
  };

  const checkResetToken = async (token: string) => {
    const response = await axiosInstance.get(`${cbtApiUrl}portal/api/checkResetToken`, {
      params: { token },
    });
    return response.data;
  };

  const resetPasswordByUser = (params: ResetPasswordDto, token: string) => {
    return axiosInstance.put(`${cbtApiUrl}portal/api/verification/reset?token=${token}`, params);
  };

  const signInToRemoteSite = (siteName: string) =>
    axiosInstance.get<Oauth2>(`${cbtApiUrl}portal/api/oauth2/${siteName}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const storageClearThenGoBack = useCallback(async () => {
    await axiosInstance.get(`${JSON.parse(getItem('defaultConfig') ?? '{}')?.cbtApiUrl}portal/api/oauth2/signOut`);
    localStorage.removeItem('currentRole');
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.clear();
    window.location.replace('/');
  }, []);

  const logout = useCallback(async () => {
    await storageClearThenGoBack();
  }, [storageClearThenGoBack]);

  const refreshAccessToken = async () => {
    try {
      if (getItem('refresh_token') !== null) {
        const response = await axiosInstance.get(
          `${JSON.parse(getItem('defaultConfig') ?? '{}')?.cbtApiUrl}portal/api/oauth2/refreshToken/${getItem('refresh_token')}`
        );
        const newToken = response.data.access_token;
        setItem('access_token', newToken);
        setToken(newToken);
        return newToken;
      }
    } catch (error) {
      console.error('Failed to refresh access token:', error);
      logout();
    }
  };

  const getUser = () => {
    axiosInstance
      .get(`${cbtApiUrl}portal/api/users/me`, {
        headers: {
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response: AxiosResponse<User>) => {
        setUser(response.data);
        setItem('currentRole', response.data.role);
      })
      .catch((error: any) => {
        if (error.response?.status === 401) {
          return Promise.reject(error);
        }
      });
  };

  const putUserUpdateMe = (namePassword: UpdatePortalUserPasswordCheckDto): Promise<AxiosResponse> =>
    axiosInstance.put(`${cbtApiUrl}portal/api/users/userUpdate/me`, namePassword, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const deleteSite = (siteId: number): Promise<AxiosResponse> =>
    axiosInstance.delete(`${cbtApiUrl}portal/api/sites/${siteId}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const getMembersByLeaderId = (leaderId: number): Promise<AxiosResponse<GetPortalMembers[]>> =>
    axiosInstance.get(`${cbtApiUrl}portal/api/users/${leaderId}/members`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const deleteMemberByEmail = (memberEmail: string): Promise<AxiosResponse> =>
    axiosInstance.delete(`${cbtApiUrl}portal/api/members/${memberEmail}/mine`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const postMemberInvite = (data: MembersMineInviteDto): Promise<AxiosResponse> =>
    axiosInstance.post(`${cbtApiUrl}portal/api/members/mine/invite`, data, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const getSites = (): Promise<AxiosResponse<SiteInfo[]>> => {
    return axiosInstance.get(`${cbtApiUrl}portal/api/sites`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const getDevicesBySiteId = (siteId: number): Promise<AxiosResponse<SiteDevices>> => {
    return axiosInstance.get(`${cbtApiUrl}portal/api/sites/${siteId}/devices`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const getComplexDevices = ({
    includeStatistics,
    includeCapabilities,
    includeBackup,
  }: {
    includeStatistics?: boolean;
    includeCapabilities?: boolean;
    includeBackup?: boolean;
  }): Promise<AxiosResponse<ComplexDeviceWithType[]>> => {
    let baseUrl = `${cbtApiUrl}api/owgw/complexDevices?`;

    if (includeStatistics !== undefined) {
      baseUrl += `includeStatistics=${includeStatistics}&`;
    }
    if (includeCapabilities !== undefined) {
      baseUrl += `includeCapabilities=${includeCapabilities}&`;
    }
    if (includeBackup !== undefined) {
      baseUrl += `includeBackup=${includeBackup}&`;
    }

    if (baseUrl.slice(-1) === '?') {
      baseUrl = baseUrl.slice(0, -1);
    }
    if (baseUrl.slice(-1) === '&') {
      baseUrl = baseUrl.slice(0, -1);
    }
    return axiosInstance.get(baseUrl, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const postCbtRedirect = (data: cbtRedirectDto): Promise<AxiosResponse> =>
    axiosInstance.post(`${cbtApiUrl}api/par-devices/cbtRedirect`, data, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const getSiteInfoById = (siteId: number): Promise<AxiosResponse<SiteInfo>> =>
    axiosInstance.get(`${cbtApiUrl}portal/api/sites/${siteId}`, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const postRontoSite = (createRontoDto: CreateRontoSiteDto): Promise<AxiosResponse<SiteInfo>> =>
    axiosInstance.post(`${cbtApiUrl}portal/api/sites`, createRontoDto, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const putSite = (siteId: number, updateSite: UpdateSiteDto): Promise<AxiosResponse<SiteInfo>> =>
    axiosInstance.put(`${cbtApiUrl}portal/api/sites/${siteId}`, updateSite, {
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${token}`,
      },
    });

  const getSystemsParDevicePortalConfig = (configs: string[]): Promise<AxiosResponse<ParDeviceConfig>> => {
    let baseUrl = `${cbtApiUrl}api/systems/parDevicePortalConfig?`;

    if (configs.length > 0) {
      let configsStr = configs.map((param) => `c=${param}`).join('&');
      baseUrl += configsStr;
    }

    return axiosInstance.get<ParDeviceConfig>(baseUrl);
  };

  const putSystemsParDevicePortalConfigQuery = (parDeviceConfigQuery: UpdateSystemParDeviceConfigDto): Promise<AxiosResponse<ParDeviceConfig>> => {
    return axiosInstance.put<ParDeviceConfig>(`${cbtApiUrl}api/systems/parDevicePortalConfig/updateQuery`, parDeviceConfigQuery);
  };

  const getSystemsMailServerSetting = (): Promise<AxiosResponse<MailServerSetting>> => {
    return axiosInstance.get<MailServerSetting>(`${cbtApiUrl}portal/api/systems/mailServer`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const putSystemsMailServerSetting = (updateMailServerSetting: UpdateSystemDto): Promise<AxiosResponse<MailServerSetting>> => {
    return axiosInstance.put<MailServerSetting>(`${cbtApiUrl}portal/api/systems/mailServer`, updateMailServerSetting, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const getPortalBackup = (): Promise<AxiosResponse> => {
    return axiosInstance.get(`${cbtApiUrl}portal/api/systems/backup`, {
      headers: {
        'content-type': 'multipart/form-data',
        Authorization: `Bearer ${token}`,
        Accept: '*/*',
      },
      responseType: 'blob',
    });
  };

  const putPortalRestore = (file: FormData): Promise<AxiosResponse> => {
    return axiosInstance.post(`${cbtApiUrl}portal/api/systems/restore`, file, {
      headers: {
        'content-type': 'multipart/form-data',
        Accept: '*/*',
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const getCheckApiIsReady = (): Promise<AxiosResponse<boolean>> => {
    return axiosInstance.get<boolean>(`${cbtApiUrl}portal/api/checkApiIsReady`);
  };

  return (
    <AuthPortalContext.Provider
      value={{
        token,
        userId,
        password,
        user,
        logout,
        signInByUser,
        signUpByUser,
        sendEmailByUser,
        checkResetToken,
        resetPasswordByUser,
        signInToRemoteSite,
        storageClearThenGoBack,
        getUser,
        putUserUpdateMe,
        deleteSite,
        getMembersByLeaderId,
        deleteMemberByEmail,
        postMemberInvite,
        getSites,
        getDevicesBySiteId,
        getComplexDevices,
        postCbtRedirect,
        getSiteInfoById,
        postRontoSite,
        putSite,
        getSystemsParDevicePortalConfig,
        putSystemsParDevicePortalConfigQuery,
        getSystemsMailServerSetting,
        putSystemsMailServerSetting,
        getPortalBackup,
        putPortalRestore,
        getCheckApiIsReady,
      }}
    >
      {children}
    </AuthPortalContext.Provider>
  );
};

export const usePortalAuth = () => React.useContext(AuthPortalContext);
